import { motion, useReducedMotion } from "framer-motion";
import * as React from "react";

export interface TypewriterLineProps {
  /** The phrases, typed one after the other in a loop. */
  items: string[];
  /** Delay between typed letters, in ms. Default 35; deleting runs faster. */
  typingSpeedMs?: number;
  /** How long a finished phrase stays, in ms. Default 1400. */
  holdMs?: number;
  className?: string;
}

/**
 * Types a list of phrases in turn, holds each one and deletes it, e.g. the suggested
 * prompts under the Tessa search. With reduced motion the phrases fade in instead. The
 * caret blinks with a `caret` keyframes animation the page has to define.
 */
export const TypewriterLine: React.FC<TypewriterLineProps> = ({
  items,
  typingSpeedMs = 35,
  holdMs = 1400,
  className
}) => {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = React.useState(0);
  const [text, setText] = React.useState("");
  const [phase, setPhase] = React.useState<"typing" | "holding" | "deleting">(
    "typing"
  );

  // Cycle index helper
  const nextIndex = React.useCallback(
    () => (index + 1) % items.length,
    [index, items.length]
  );

  React.useEffect(() => {
    if (items.length === 0) return;

    if (reduceMotion) {
      // Reduced motion: simple crossfade between full strings
      const t = setTimeout(
        () => setIndex((i) => (i + 1) % items.length),
        holdMs + 600
      );
      return () => clearTimeout(t);
    }

    const current = items[index];

    if (phase === "typing") {
      if (text.length < current.length) {
        const t = setTimeout(
          () => setText(current.slice(0, text.length + 1)),
          typingSpeedMs
        );
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setPhase("holding"), holdMs);
      return () => clearTimeout(t);
    }

    if (phase === "holding") {
      const t = setTimeout(() => setPhase("deleting"), 250);
      return () => clearTimeout(t);
    }

    if (phase === "deleting") {
      if (text.length > 0) {
        const t = setTimeout(
          () => setText(text.slice(0, -1)),
          Math.max(typingSpeedMs * 0.6, 16)
        );
        return () => clearTimeout(t);
      }
      setPhase("typing");
      setIndex(nextIndex());
    }

    return;
  }, [
    items,
    index,
    text,
    phase,
    typingSpeedMs,
    holdMs,
    reduceMotion,
    nextIndex
  ]);

  if (items.length === 0) return null;

  const visible = reduceMotion ? items[index] : text;

  return (
    <div className={["font-medium", className ?? ""].join(" ")}>
      {reduceMotion ? (
        <motion.span
          key={index}
          initial={{ opacity: 0, y: 2 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -2 }}
          transition={{ duration: 0.35 }}
          className="inline-block"
        >
          {visible}
        </motion.span>
      ) : (
        <>
          {/* Read out whole: the letters typed so far would be read as fragments */}
          <span className="sr-only">{items[index]}</span>
          <span aria-hidden className="inline-block">
            {visible}
          </span>
          <span
            aria-hidden
            className="ml-0.5 inline-block h-[1.25em] w-[1px] bg-foreground/80 align-[-0.2em] [animation:caret_1.2s_steps(2,end)_infinite]"
          />
        </>
      )}
    </div>
  );
};
